/**
 * 동기화 상태 바 컴포넌트
 * 프로젝트 목록 상단에 표시되어 동기화 상태를 보여줌
 */

import { useEffect, useState } from "react";

export interface SyncStatusBarProps {
  /** 대기 중인 동기화 항목 수 */
  pendingCount: number;
  /** 동기화 진행 중 여부 */
  isSyncing: boolean;
  /** 현재 진행 중인 항목 수 */
  progress: number;
  /** 전체 항목 수 */
  total: number;
  /** 동기화 버튼 클릭 핸들러 */
  onSyncClick: () => void;
  /** 오프라인일 때 비활성화 */
  disabled?: boolean;
}

export function SyncStatusBar({
  pendingCount,
  isSyncing,
  progress,
  total,
  onSyncClick,
  disabled = false,
}: SyncStatusBarProps) {
  // 완료 후 fade out 상태 관리
  const [showCompleted, setShowCompleted] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  // 동기화 완료 시 fade out 처리
  useEffect(() => {
    if (pendingCount === 0 && !isSyncing && total > 0) {
      // 동기화가 완료되면 "완료" 메시지 표시
      setShowCompleted(true);
      setIsVisible(true);

      // 2초 후 fade out
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 2000);

      // fade out 애니메이션 후 숨김
      const hideTimer = setTimeout(() => {
        setShowCompleted(false);
      }, 2500);

      return () => {
        clearTimeout(timer);
        clearTimeout(hideTimer);
      };
    } else {
      setShowCompleted(false);
      setIsVisible(true);
    }
  }, [pendingCount, isSyncing, total]);

  // 표시할 내용이 없으면 렌더링하지 않음
  if (pendingCount === 0 && !isSyncing && !showCompleted) {
    return null;
  }

  // 프로그레스 퍼센트 계산
  const progressPercent = total > 0 ? Math.round((progress / total) * 100) : 0;

  // 완료 상태 렌더링
  if (showCompleted) {
    return (
      <div
        className={`flex min-h-[44px] items-center gap-2 rounded-lg bg-green-50 px-4 py-3 transition-opacity duration-500 ${
          isVisible ? "opacity-100" : "opacity-0"
        }`}
        role="status"
        aria-live="polite"
      >
        <svg
          className="h-5 w-5 text-green-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <span className="text-sm font-medium text-green-700">동기화 완료</span>
      </div>
    );
  }

  // 동기화 진행 중 렌더링
  if (isSyncing) {
    return (
      <div
        className="flex min-h-[44px] flex-col gap-2 rounded-lg bg-blue-50 px-4 py-3"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div className="flex items-center gap-2">
          {/* 회전하는 동기화 아이콘 */}
          <svg
            className="h-5 w-5 animate-spin text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <span className="text-sm font-medium text-blue-700">
            {progress}/{total} 동기화 중...
          </span>
        </div>

        {/* 프로그레스 바 */}
        <div className="w-full">
          <div className="h-2 w-full overflow-hidden rounded-full bg-blue-200">
            <div
              className="h-full rounded-full bg-blue-600 transition-all duration-300 ease-out"
              style={{ width: `${progressPercent}%` }}
              role="progressbar"
              aria-valuenow={progressPercent}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
          <span className="mt-1 block text-right text-xs text-blue-600">{progressPercent}%</span>
        </div>
      </div>
    );
  }

  // 대기 상태 렌더링 (pendingCount > 0)
  return (
    <div
      className="flex min-h-[44px] items-center justify-between rounded-lg bg-blue-50 px-4 py-3"
      role="status"
    >
      <div className="flex items-center gap-2">
        {/* 동기화 아이콘 */}
        <svg
          className="h-5 w-5 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        <span className="text-sm font-medium text-blue-700">대기 {pendingCount}개</span>
      </div>

      {/* 동기화 버튼 */}
      <button
        onClick={onSyncClick}
        disabled={disabled}
        className={`min-h-[36px] rounded-md px-4 py-2 text-sm font-medium transition-colors ${
          disabled
            ? "cursor-not-allowed bg-gray-300 text-gray-500"
            : "bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800"
        }`}
        aria-label={disabled ? "오프라인 상태에서는 동기화할 수 없습니다" : "동기화 시작"}
      >
        동기화
      </button>
    </div>
  );
}

export default SyncStatusBar;
